/** Map of `{name}`-style placeholder keys to their substitution values. */
export type TranslationVars = Record<string, string | number>;

/**
 * Substitute `{name}`-style placeholders in `template` with values from
 * `vars`. Numbers are stringified via `String(value)`. Unknown placeholders
 * are left intact (the literal `{name}` stays in the output) so that
 * translator typos surface visibly in the UI rather than being silently
 * swallowed.
 *
 * Most code does not need to call this directly — {@link useTranslation}'s
 * `t(key, vars)` applies it for you. Reach for `interpolate` when you have a
 * string outside the hook (an API error message, a `Messages`-shaped value
 * read manually) that you still want to template against local values.
 *
 * @example
 *   interpolate('Synced on {date}', { date: '2026-05-11' });
 *   // → "Synced on 2026-05-11"
 *   interpolate('Hi {name}', {}); // → "Hi {name}" (placeholder left intact)
 */
export function interpolate(template: string, vars: TranslationVars): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  );
}
